import React, { useRef, useState } from 'react';
import { useHistory, useRouteMatch } from 'react-router-dom';
import useAxios from 'axios-hooks';

import Alert from '@react/react-spectrum/Alert';
import Button from '@react/react-spectrum/Button';
import Dialog from '@react/react-spectrum/Dialog';
import FieldLabel from '@react/react-spectrum/FieldLabel';
import Heading from '@react/react-spectrum/Heading';
import Textfield from '@react/react-spectrum/Textfield';
import Wait from '@react/react-spectrum/Wait';

import Launch from '@react/react-spectrum/Icon/Launch';
import BoxAdd from '@react/react-spectrum/Icon/BoxAdd';
import Refresh from '@react/react-spectrum/Icon/Refresh';

import { PATHS } from 'config/constants';
import { useAEP } from 'data/hooks';
import LaunchPropertyList from 'components/LaunchPropertyList';
import ProjectList from 'components/ProjectList';
import SandboxSnapshotList from 'components/SandboxSnapshotList';

import './AEPDetails.scss';
import Properties from './Properties';

export default function AEPDetails({}) {
  const { params } = useRouteMatch();
  const { loading, error, data } = useAEP(params.id);

  const sandboxSnapshotList = useRef(null);

  if (loading) {
    return (
      <div className="aep-details">
        <Wait centered />
      </div>
    );
  }

  if (error) {
    return (
      <div className="aep-details">
        <Alert variant="error" header="Error">
          {error.message}
        </Alert>
      </div>
    );
  }

  const { imsOrg, launch, sandboxName } = data.aep;

  return (
    <div className="aep-details">
      <div className="header">
        <div className="title">
          <img src="https://demo-system-next.s3.eu-north-1.amazonaws.com/logos/aep.svg" />
          <span>
            <Heading size={1}>{sandboxName}</Heading>
            <Heading size={5}>AEP Sandbox</Heading>
          </span>
        </div>
        <SandboxActions orgId={imsOrg.id} sandboxName={sandboxName} />
      </div>

      <Properties id={params.id} />

      <div className="panels">
        <section className="projects">
          <Heading variant="subtitle1">Projects</Heading>
          <p className="description">
            All Experience Builder projects connected to this sandbox
          </p>
          <ProjectList orgID={imsOrg.id} sandboxName={sandboxName} />
        </section>

        <section className="launch-properties">
          <Heading variant="subtitle1">Data Collection properties</Heading>
          <p className="description">
            All Data Collection properties connected to this sandbox
          </p>
          <LaunchPropertyList data={launch} />
        </section>

        <section className="snapshots">
          <div className="actions">
            <Heading variant="subtitle1">Snapshots</Heading>
            <Button
              icon={<Refresh size="S" />}
              variant="tool"
              onClick={() => sandboxSnapshotList.current.refresh()}
            />
          </div>
          <SandboxSnapshotList
            ref={sandboxSnapshotList}
            orgId={imsOrg.id}
            sandboxName={sandboxName}
          />
        </section>
      </div>
    </div>
  );
}

function SandboxActions({ orgId, sandboxName }) {
  const history = useHistory();
  const [dialogIsOpen, setDialogIsOpen] = useState(false);

  return (
    <div className="actions">
      <Button
        icon={<Launch />}
        quiet
        onClick={() => history.push(PATHS.QUICK_SETUP)}>
        Quick Setup
      </Button>

      <Button icon={<BoxAdd />} quiet onClick={() => setDialogIsOpen(true)}>
        Create snapshot
      </Button>
      <CreateSnapshotDialog
        isOpen={dialogIsOpen}
        close={() => setDialogIsOpen(false)}
        orgId={orgId}
        sandboxName={sandboxName}
      />
    </div>
  );
}

function CreateSnapshotDialog({ isOpen, close, orgId, sandboxName }) {
  const [snapshotName, setSnapshotName] = useState(null);
  const [{ data, loading, error }, execute] = useAxios(
    {
      url: `${process.env.REACT_APP_API_ROOT}/aep/snapshots/create`,
      method: 'POST'
    },
    { manual: true }
  );

  const initialState = !data && !error;

  const onConfirm = () => {
    if (!initialState) {
      close();
      return;
    }

    execute({
      data: {
        sandboxName,
        orgId,
        snapshotName
      }
    });
  };

  return (
    isOpen && (
      <div className="spectrum-Underlay is-open">
        <Dialog
          className="create-snapshot-dialog"
          title="Create snapshot"
          confirmLabel={initialState ? 'Create' : 'Close'}
          cancelLabel={initialState ? 'Cancel' : null}
          onConfirm={onConfirm}
          onCancel={() => close()}>
          {loading && (
            <div className="wait-container">
              <Wait centered />
            </div>
          )}

          {data && (
            <div className="result-container">
              <Alert variant="success" header="Success">
                Sandbox snapshot request was successfully created.
                <br />
                Depending on the contents of your sandbox, it usually takes
                between 10 seconds to few minutes.
              </Alert>
            </div>
          )}

          {error && (
            <div className="result-container">
              <Alert variant="error" header="Error">
                An error occured when trying to create a snapshot.
              </Alert>
            </div>
          )}

          {initialState && !loading && (
            <>
              <Alert variant="info" header="Create snapshot">
                This operation will create a full snapshot of selected sandbox.
                <br />
                Once the snapshot is ready, it can be imported to any empty
                sandbox.
              </Alert>

              <FieldLabel label="Snapshot name">
                <Textfield
                  defaultValue={snapshotName}
                  onChange={(value) => setSnapshotName(value)}
                />
              </FieldLabel>
            </>
          )}
        </Dialog>
      </div>
    )
  );
}
